import { useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCompanyCode, hasSession, isSuperAdmin } from "./auth/authSession";
import { PermissionContext } from "./PermissionContext";
import { isAllowedDepartmentQuery } from "./utils/departmentAccess";
import { departmentIdFromLocation } from "./utils/procurementMode";

const mergeDepartments = (inventory, nonInventory) => {
  const byId = new Map();
  [...(Array.isArray(inventory) ? inventory : []), ...(Array.isArray(nonInventory) ? nonInventory : [])].forEach(
    (dept) => {
      if (dept?.id != null) byId.set(dept.id, dept);
    }
  );
  return [...byId.values()];
};

const DepartmentQueryGuard = ({ children, lane }) => {
  const location = useLocation();
  const { permissions, departmentAccess, loading } = useContext(PermissionContext);
  const [allowed, setAllowed] = useState(null);
  const departmentId = departmentIdFromLocation(location);
  const resolvedLane =
    lane ||
    (location.pathname.startsWith("/non-inventory")
      ? "NON_INVENTORY"
      : "INVENTORY");

  useEffect(() => {
    setAllowed(null);
  }, [departmentId, resolvedLane]);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (departmentId == null) {
        if (!cancelled) setAllowed(true);
        return;
      }
      if (loading) return;
      if (
        isAllowedDepartmentQuery({
          departmentId,
          departmentAccess,
          departments: [],
          lane: resolvedLane,
          unionPermissions: permissions,
        }) &&
        (departmentAccess?.unrestricted || departmentAccess?.wildcard)
      ) {
        if (!cancelled) setAllowed(true);
        return;
      }

      const companyCode = getCompanyCode();
      if (!hasSession() || !companyCode) {
        if (!cancelled) setAllowed(false);
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const [invRes, nonInvRes] = await Promise.all([
          fetch(`${apiUrl}/api/department/all/${companyCode}?lane=INVENTORY`),
          fetch(`${apiUrl}/api/department/all/${companyCode}?lane=NON_INVENTORY`),
        ]);
        const inventory = invRes.ok ? await invRes.json() : [];
        const nonInventory = nonInvRes.ok ? await nonInvRes.json() : [];
        const ok = isAllowedDepartmentQuery({
          departmentId,
          departmentAccess,
          departments: mergeDepartments(inventory, nonInventory),
          lane: resolvedLane,
          unionPermissions: permissions,
        });
        if (!cancelled) setAllowed(ok);
      } catch {
        if (!cancelled) setAllowed(false);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [departmentId, departmentAccess, loading, permissions, resolvedLane]);

  if (departmentId == null) return children;
  if (isSuperAdmin()) return children;
  if (allowed == null) return <div>Loading...</div>;
  if (!allowed) return <Navigate to="/unauthorized" replace />;
  return children;
};

export default DepartmentQueryGuard;
